import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { IoIosAdd, IoIosRemove } from 'react-icons/io';
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import CustomAddModal from '../../../components/Common/CustomAddModal';
import CustomPagination from '../../../components/Common/CustomPagination';
import { getCurrentUserId, getCurrentUserOrganizationId, toastError, toastSuccess } from '../../../utils';
import { getAddressDetails } from '../AddressBook/actions';
import { getFuelSupplyDetails, updateFuelSupplyRequest } from './actions';


const allHeaders = [
  'Request ID',
  'Request Date',
  'Request Type',
  'Vessel Name',
  'Vessel Code',
  'Ops ID',
  'Voyage Number',
  'Fuel Specification',
  'Fuel Usage Type',
  'Port of Supply',
  'Agents',
  'Requested Quantity',
  'Committed Price',
  'Supplied Qty',
  'Purchase Price',
  'Difference',
  'Remarks / Comments',
  'Bunker Manager',
];


const FuelSupply = (props) => {
  const { loader = false, setLoader = () => { } } = props;
  const [loading, setLoading] = useState(false);
  const [counterPartyDetails, setCounterPartyDetails] = useState();
  const scrollRef = useRef(null);

  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalItems: 0,
    offset: 1,
    limit: 50,
    sortBy: "requestedDate",
    sortDirection: "desc",
  });
  const [data, setData] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleHeaders, setVisibleHeaders] = useState(
    allHeaders.reduce((acc, header, index) => {
      acc[header] = index < 8;
      return acc;
    }, {})
  );


  const headers = allHeaders.filter(header => visibleHeaders[header]);
  const open = Boolean(anchorEl);
  const [isOpen, setIsOpen] = useState({
    isOpen: false,
    data: {},
  });

  const transformAddressItems = (list = []) => {
    return list.map(item => {
      let fullName = "";

      if (item.type === "Individual") {
        const { individual_firstName = "", individual_middleName = "", individual_lastName = "" } = item;
        fullName = [individual_firstName, individual_middleName, individual_lastName].filter(Boolean).join(" ");
      } else if (item.type === "Organisation") {
        fullName = item.organisation_fullName || "";
      }

      return {
        _id: item._id,
        type: item.type,
        fullName,
      };
    });
  };

  const fetchCounterPartyDetails = async () => {
    const userId = getCurrentUserId();

    try {
      const userRes = await getAddressDetails({ userId, addressBookType: "organization", perPage: 100 })
      setCounterPartyDetails(transformAddressItems(userRes?.data?.list));
    } catch (error) {
      console.error("Failed to fetch counter party details:", error);
      setCounterPartyDetails([]);
    }
  };

  const getAvailableCounterParties = (field, currentIndex) => {
    const selectedItems = isOpen?.data?.[field] || [];
    const allCounterParties = counterPartyDetails || [];

    const currentSelectedId = selectedItems[currentIndex]?.id;

    const selectedIds = selectedItems
      .map((item, index) => (index !== currentIndex ? item.id : null))
      .filter((id) => id != null);

    let available = allCounterParties.filter((item) =>
      !selectedIds.includes(item._id) || item._id === currentSelectedId
    );
    if (currentSelectedId && !available.some(item => item._id === currentSelectedId)) {
      const currentItem = selectedItems[currentIndex];
      available = [{
        _id: currentSelectedId,
        fullName: currentItem.name || "Unknown Agent",
        type: currentItem.type || "Unknown Type"
      }, ...available];
    }

    return available;
  };

  const hasData = counterPartyDetails?.length > 0;

  useEffect(() => {
    fetchCounterPartyDetails();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleHeader = (header) => {
    setVisibleHeaders(prev => ({
      ...prev,
      [header]: !prev[header]
    }));
  };

  const fetchData = async () => {
    setLoading(true);
    setLoader(true);
    const response = await getFuelSupplyDetails({
      offset: pagination.offset,
      limit: pagination.limit,
      sortBy: pagination.sortBy,
      sortDirection: pagination.sortDirection,
      id: getCurrentUserOrganizationId()
    });
    setData(response.data);
    setPagination((prev) => ({
      ...prev,
      totalPages: response.totalPages,
      totalItems: response.totalItems,
    }));
    setLoading(false);
    setLoader(false);
    setHasFetched(true);
  };
  useEffect(() => {
    fetchData();
  }, [pagination.offset, pagination.sortBy, pagination.sortDirection]);

  const handleSortClick = (sortKey) => {
    let newSortDirection = "asc";
    if (pagination.sortBy === sortKey) {
      if (pagination.sortDirection === "asc") newSortDirection = "desc";
      else if (pagination.sortDirection === "desc") {
        newSortDirection = sortKey === "requestedDate" ? "asc" : "";
      }
    } else {
      newSortDirection = sortKey === "requestedDate" ? "desc" : "asc";
    }

    let nextSortBy = sortKey;
    if (!newSortDirection) {
      nextSortBy = "requestedDate";
      newSortDirection = "desc";
    }

    setPagination((prev) => ({
      ...prev,
      offset: 1,
      sortBy: nextSortBy,
      sortDirection: newSortDirection,
    }));
  };

  const onChangePagination = (type, key) => {
    setPagination((prev) => {
      const currentPage = prev.offset || 1;
      const limit = prev.limit || 10;

      let newOffset = currentPage;

      if (type === "first") {
        newOffset = 1;
      } else if (type === "dec" && currentPage > 1) {
        newOffset = currentPage - 1;
      } else if (type === "inc" && currentPage * limit < pagination.totalItems) {
        newOffset = currentPage + 1;
      }

      return {
        ...prev,
        offset: newOffset,
      };
    });
  };

  const handleView = (row) => {
    setIsOpen({
      isOpen: true,
      data: row,
    })
  }

  const handleUpdateRequest = async () => {
    try {
      await updateFuelSupplyRequest({ id: isOpen.data.operationId }, isOpen.data);
      toastSuccess("Fuel supply request updated successfully");
      setIsOpen({
        isOpen: false,
        data: {},
      })
      fetchData();
    } catch (error) {
      toastError(error?.message || "Failed to update fuel supply request");
    }
  }

  const addItemAtIndex = (field, index) => {
    setIsOpen(prev => {
      const updated = [...(prev.data[field] ?? [])];
      updated.splice(index + 1, 0, { name: "", type: "", id: "" });
      return { ...prev, data: { ...prev.data, [field]: updated } };
    });
  };

  const deleteItemAtIndex = (field, index) => {
    setIsOpen(prev => {
      const updated = [...(prev.data[field] ?? [])];
      updated.splice(index, 1);
      return { ...prev, data: { ...prev.data, [field]: updated } };
    });
  };

  const updateItemAtIndex = (field, index, value) => {
    setIsOpen(prev => {
      const updated = [...(prev.data[field] ?? [])];
      const selectedCounterParty = counterPartyDetails?.find(item => item._id === value);

      updated[index] = {
        ...updated[index],
        id: value,
        name: selectedCounterParty?.fullName || "",
        type: selectedCounterParty?.type || ""
      };

      return { ...prev, data: { ...prev.data, [field]: updated } };
    });
  };

  const handleInputChange = (field, value) => {
    setIsOpen(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  return (
    <div className="w-full min-h-[calc(100vh-110px)]">
      <div className="flex justify-start mb-2">
        <div className="relative">
          <button
            onClick={handleClick}
            className="h-7 px-3 bg-[#313C4C] hover:bg-[#0a2a47] text-white text-[11px] font-medium rounded-none flex items-center justify-center gap-1.5 shadow-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Select Columns
          </button>

          {open && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={handleClose}
              />

              {/* Popover */}
              <div className="absolute z-20 mt-1 left-0 bg-white rounded-none shadow-lg border border-gray-300 min-w-48">
                <div className="h-full overflow-y-auto">
                  <div className="space-y-1.5 p-3 h-fit max-h-[50vh] overflow-y-auto scroll-thin">
                    {allHeaders.map(header => (
                      <label
                        key={header}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 text-[11px] font-medium"
                      >
                        <input
                          type="checkbox"
                          checked={visibleHeaders[header]}
                          onChange={() => handleToggleHeader(header)}
                          className="w-3.5 h-3.5 text-[#313C4C] bg-gray-100 border-gray-300 rounded-none focus:ring-[#313C4C]/20"
                        />
                        <span className="text-[11px] font-medium text-gray-700 select-none">
                          {header}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded-none border border-slate-300 shadow-sm">
        <TableContainer component={Paper} className="!rounded-none !shadow-none">
          {pagination?.totalItems > pagination?.limit && (
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={pagination}
              totalPage={pagination.totalItems}
            />
          )}
          <Table className="universal-table w-full">
            <TableHead className="bg-[#313C4C]">
              <TableRow>
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  S.No
                </TableCell>
                {headers.map((header, index) => {
                  const sortMap = {
                    'Voyage Number': 'voyageNo',
                    'Ops ID': 'opsId',
                    'Vessel Code': 'vesselCode',
                    'Vessel Name': 'vesselName',
                    'Request Type': 'requestType',
                    'Request Date': 'requestedDate',
                  };
                  const sortKey = sortMap[header];
                  return (
                    <TableCell
                      key={header}
                      className={`text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 leading-tight ${sortKey ? 'cursor-pointer' : ''}`}
                      onClick={() => sortKey ? handleSortClick(sortKey) : undefined}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {header}
                        {sortKey && (
                          <div style={{ color: pagination.sortBy === sortKey ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                            {pagination.sortBy === sortKey && pagination.sortDirection === "desc" ? <LuArrowDown size={13} /> : pagination.sortBy === sortKey && pagination.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  )
                })}
                <TableCell
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loader ? (
                <TableRow>
                  <TableCell colSpan={headers.length + 2} align="center" className="!bg-white">
                    <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                    </Box>
                  </TableCell>
                </TableRow>
              ) : data && data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow
                    key={row._id || index}
                    className="h-7 hover:bg-gray-50 cursor-pointer"
                    onDoubleClick={() => handleView(row)}
                    sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}
                  >
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium text-center border-r border-gray-300">
                      {(pagination.offset - 1) * pagination.limit + index + 1}
                    </TableCell>
                    {visibleHeaders['Request ID'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.requestId || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Request Date'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.requestedDate && moment(row.requestedDate).isValid() ? moment(row.requestedDate).format("DD-MM-YYYY") : "-"}
                      </TableCell>
                    )}
                    {visibleHeaders['Request Type'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.requestType || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Vessel Name'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.vesselName || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Vessel Code'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.vesselCode || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Ops ID'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.opsId || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Voyage Number'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.voyageNo || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Fuel Specification'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.fuelSpecification || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Fuel Usage Type'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.fuelUsageType || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Port of Supply'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.port || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Agents'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        <div className="max-w-[200px] truncate" title={row.agents}>
                          {row.agents?.length || 0}
                        </div>
                      </TableCell>
                    )}
                    {visibleHeaders['Requested Quantity'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.reqQuantity || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Committed Price'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.committedPrice || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Supplied Qty'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.suppliedQty || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Purchase Price'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.purchasePrice || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Difference'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300 text-right">
                        {row.difference || '-'}
                      </TableCell>
                    )}
                    {visibleHeaders['Remarks / Comments'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        <div className="max-w-[250px] truncate" title={row.remarks}>
                          {row.remarks || '-'}
                        </div>
                      </TableCell>
                    )}
                    {visibleHeaders['Bunker Manager'] && (
                      <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                        {row.bunkerManager || '-'}
                      </TableCell>
                    )}
                    <TableCell className="!py-1 !px-2 text-center border-gray-300">
                      <button
                        onClick={() => handleView(row)}
                        className="p-1 text-[#313C4C] hover:text-[#0a2a47] cursor-pointer"
                        title="View"
                      >
                        <FaEye style={{ fontSize: 14 }} color="#313C4C" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : hasFetched ? (
                <TableRow>
                  <TableCell colSpan={headers.length + 2} className="!bg-white text-center py-10">
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2.5">
                        <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <p className="text-[11px] font-medium text-slate-700 mb-0.5">No fuel supply data available</p>
                      <p className="text-[10px] font-medium text-slate-400">There are no fuel supply records to display at this time.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          {pagination?.totalItems > pagination?.limit && (
            <CustomPagination
              onChangePagination={onChangePagination}
              filters={pagination}
              totalPage={pagination.totalItems}
            />
          )}
        </TableContainer>
      </div>

      <CustomAddModal
        open={isOpen.isOpen}
        handleClose={() => setIsOpen({
          isOpen: false,
          data: {}
        })}
        title="Fuel Supply Request"
        maxWidth="md"
        detailsTitle="View Fuel Supply Request"
        onSubmitConfirm={handleUpdateRequest}
        saveLabel="Update Request"
      >
        <div className="space-y-2.5 text-[11px] font-medium">
          <div className="grid grid-cols-3 gap-2.5">
            <div>
              <div className="mb-2">
                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Request Type</label>
                <select
                  value={isOpen?.data.requestType}
                  onChange={(e) => handleInputChange('requestType', e.target.value)}
                  className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                >
                  <option value="">Select request type</option>
                  <option value="Firm Request">Firm Request</option>
                  <option value="Intimation">Intimation</option>
                  <option value="Hedging Request">Hedging Request</option>
                </select>
              </div>

              {[{
                label: "Vessel Name",
                value: "vesselName",
              }, {
                label: "Vessel Code",
                value: "vesselCode",
              }, {
                label: "Ops ID",
                value: "opsId",
              }, {
                label: "Voyage Number",
                value: "voyageNo",
              }].map(field => (
                <div key={field.value} className="mb-2">
                  <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{field.label}</label>
                  <input
                    type="text"
                    value={isOpen?.data[field.value] || ''}
                    disabled
                    className="w-full h-7 px-2 py-1 border border-slate-200 bg-slate-100 text-slate-600 rounded-none text-[11px] font-medium cursor-not-allowed"
                    placeholder=""
                  />
                </div>
              ))}
            </div>

            <div>
              <div className="mb-2">
                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Port</label>
                <input
                  type="text"
                  value={isOpen?.data.port || ''}
                  disabled
                  className="w-full h-7 px-2 py-1 border border-slate-200 bg-slate-100 text-slate-600 rounded-none text-[11px] font-medium cursor-not-allowed"
                  placeholder=""
                />
              </div>
              <div className="mb-2">
                <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Fuel Usage Type</label>
                <select
                  value={isOpen?.data.fuelUsageType}
                  onChange={(e) => handleInputChange('fuelUsageType', e.target.value)}
                  className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                >
                  <option value="">Select fuel usage type</option>
                  <option value="Main">Main</option>
                  <option value="ECA">ECA</option>
                  <option value="Light Fuel (MGO)">Light Fuel (MGO)</option>
                </select>
              </div>
              {[
                { label: 'Fuel Specification', field: 'fuelSpecification' },
                { label: 'Requested Quantity (MT)', field: 'reqQuantity', type: 'number' },
                { label: 'Committed Price', field: 'committedPrice', type: 'number' },
              ].map(({ label, field, type = 'text' }) => (
                <div key={field} className="mb-2">
                  <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{label}</label>
                  <input
                    type={type}
                    value={isOpen?.data[field] || ''}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>

            <div>
              {[
                { label: 'Supplied Qty', field: 'suppliedQty', type: 'number' },
                { label: 'Purchase Price', field: 'purchasePrice', type: 'number' },
                { label: 'Difference', field: 'difference', type: 'number' },
                { label: 'Bunker Manager', field: 'bunkerManager' },
              ].map(({ label, field, type = 'text' }) => (
                <div key={field} className="mb-2">
                  <label className="block text-[11px] font-medium text-slate-700 mb-0.5">{label}</label>
                  <input
                    type={type}
                    value={isOpen?.data[field] || ''}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    className="w-full h-7 px-2 py-1 border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C]"
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1">
            <div
              ref={scrollRef}
              style={{ overscrollBehavior: 'contain' }}
              className="flex-1 border border-slate-200 rounded-none max-h-[160px] overflow-y-auto p-2 scroll-thin bg-slate-50/50"
            >
              <div className="flex justify-center items-center w-full mb-1.5">
                <h2 className="block text-[11px] text-center font-medium uppercase tracking-wider text-slate-700 m-0">Agents</h2>
              </div>

              {!hasData ? (
                <div className="flex flex-col items-center justify-center py-2 px-3 text-center">
                  <h3 className="text-[11px] font-medium text-slate-700 mb-0.5">
                    No Agents found
                  </h3>
                  <p className="text-[11px] text-slate-400 mb-2 max-w-xs leading-tight italic">
                    You don't have any Agents in your address book yet.
                  </p>
                  <button
                    type="button"
                    onClick={() => (window.location.href = '/addressBook')}
                    className="inline-flex items-center h-6 px-2.5 text-[11px] font-medium text-white bg-[#313C4C] rounded-none hover:bg-[#1e2632] transition-colors cursor-pointer"
                  >
                    Create Agent
                  </button>
                </div>
              ) : (
                <div className="space-y-1">
                  {(isOpen?.data?.agents?.length ? isOpen?.data?.agents : [{ name: "", type: "" }]).map(
                    (item, index) => {
                      return (<div key={item.id || `agents-${index}`} className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={getAvailableCounterParties('agents', index).length === isOpen?.data?.agents?.length}
                          onClick={() => addItemAtIndex('agents', index)}
                          className="h-7 w-7 text-slate-600 hover:text-slate-900 border border-slate-300 rounded-none bg-white flex items-center justify-center cursor-pointer text-sm"
                          title="Add below"
                        >
                          <IoIosAdd />
                        </button>
                        <select
                          required
                          value={item.id || ""}
                          onChange={(e) => updateItemAtIndex('agents', index, e.target.value)}
                          className="flex-1 h-7 px-2 py-1 text-[11px] font-medium border border-slate-300 rounded-none focus:outline-none focus:border-[#313C4C] bg-white"
                        >
                          <option value="">Select Agent</option>
                          {getAvailableCounterParties('agents', index).map(({ _id, fullName, type }) => (
                            <option key={_id} value={_id}>
                              {fullName} ({type})
                            </option>
                          ))}
                        </select>
                        {isOpen?.data?.agents?.length > 1 && (
                          <button
                            type="button"
                            onClick={() => deleteItemAtIndex('agents', index)}
                            className="h-7 w-7 text-rose-600 hover:text-rose-700 border border-slate-300 rounded-none bg-white flex items-center justify-center cursor-pointer text-sm"
                            title="Delete"
                          >
                            <IoIosRemove />
                          </button>
                        )}
                      </div>);
                    }
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mb-2 flex-1">
            <label className="block text-[11px] font-medium text-slate-700 mb-0.5">Remarks</label>
            <textarea
              value={isOpen?.data.remarks || ''}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              rows={4}
              className="w-full px-2 py-1 scroll-thin border border-slate-300 bg-white text-slate-800 rounded-none text-[11px] font-medium resize-y focus:outline-none focus:border-[#313C4C] min-h-[75px]"
              placeholder="Enter remarks..."
            />
          </div>

        </div>
      </CustomAddModal>
    </div>
  );
};

export default FuelSupply;